import { useEffect, useRef } from "react";
import { useAuthStore, selectIsAuthenticated } from "@safers/auth";
import { STOMP_DESTINATIONS } from "@safers/api";
import type { EventMessage } from "@safers/api";
import { stompService } from "../services/stomp.service";
import type { ConnectionStatus } from "../services/stomp.service";
import { useEventsStore } from "../stores/events.store";

export function useStompEvents() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const addStompEvent = useEventsStore((s) => s.addStompEvent);
  const clearStompEvents = useEventsStore((s) => s.clearStompEvents);
  const setConnectionStatus = useEventsStore((s) => s.setConnectionStatus);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    /*
      로그아웃하면 버퍼를 비운다. 이 훅은 `App` 최상단에 상시 마운트라 계정 전환이
      여기를 반드시 지난다 — 스토어는 모듈 레벨이라 두지 않으면 앞 계정의 이벤트가
      다음 계정 알림함에 남는다.
    */
    if (!isAuthenticated) {
      clearStompEvents();
      return;
    }

    const onStatus = (status: ConnectionStatus) => {
      setConnectionStatus(status);

      if (status === "connected") {
        unsubscribeRef.current?.();
        unsubscribeRef.current = stompService.subscribe(STOMP_DESTINATIONS.events, (message) => {
          try {
            const event: EventMessage = JSON.parse(message.body);
            addStompEvent(event);
          } catch {
            return;
          }
        });
      }
    };

    stompService.connect(onStatus);

    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      stompService.disconnect(onStatus);
    };
  }, [isAuthenticated, addStompEvent, clearStompEvents, setConnectionStatus]);
}
